import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { CloseButton, Input, InputProps, Popover, PopoverProps } from '@mantine/core';
import { DatePicker, DatePickerProps } from '@mantine/dates';
import { assignRef, useMergedRef } from '@mantine/hooks';
import { MdCalendarMonth } from 'react-icons/md';
import { dateToIsoDate, isoDateToRu, parseRuDate } from '../../lib/dateFormValue';
import { POPOVER_Z_INDEX } from '../../lib/zIndex';
import { usePortalTarget } from '../../provider/UiContext';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { useSyncedMask } from '../../hooks/useSyncedMask';
import MantineDescribedBy from '../../theme/MantineDescribedBy';

export interface IMaskedDateInputProps {
    /** ISO date YYYY-MM-DD или null */
    value?: string | null;
    onChange?: (next: string | null) => void;
    minDate?: DatePickerProps['minDate'];
    maxDate?: DatePickerProps['maxDate'];
    excludeDate?: DatePickerProps['excludeDate'];
    invalid?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    clearable?: boolean;
    /** При наличии очистки скрыть правую иконку календаря: поле по-прежнему открывает его по клику. */
    compactActionsWhenClear?: boolean;
    placeholder?: string;
    name?: string;
    /** id поля ввода — для `<label htmlFor>` */
    id?: string;
    variant?: InputProps['variant'];
    className?: string;
    leftSection?: ReactNode;
    /** Свои настройки поповера календаря; хост портала по умолчанию — `portalTarget` из `UiProvider`. */
    popoverProps?: Partial<PopoverProps>;
    clearButtonProps?: Record<string, unknown>;
    'data-testid'?: string;
    'aria-label'?: string;
    /** id подписи поля; в `FormGroup` по умолчанию — её подпись */
    'aria-labelledby'?: string;
    /** id описания; в `FormGroup` к нему добавляются сообщение под полем и пояснение группы */
    'aria-describedby'?: string;
    /** Ссылка на поле ввода — фокус поля формы (`setFocus`, ошибка валидации) */
    ref?: React.Ref<HTMLInputElement>;
    /** Сторона иконки календаря; 'right' освобождает левый слот поля. */
    calendarPosition?: 'left' | 'right';
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    w?: InputProps['w'];
}

const DATE_MASK = '99.99.9999';

/**
 * Масочное поле даты: ручной ввод DD.MM.YYYY через @mantine/hooks useMask
 * (посимвольный Backspace, авто-разделители) + календарь Mantine DatePicker в Popover.
 *
 * Замена Mantine DateInput, у которого нет маски ввода. useMask владеет <input>
 * целиком (перехватывает keydown, сам пишет value), поэтому совместить его с
 * controlled-value DateInput нельзя — здесь input неконтролируемый, а внешний
 * ISO-value синхронизируется в текст императивно (pushText) и наоборот через
 * onChangeRaw. На readonly/disabled маску не вешаем — обычный Input.
 */
const MaskedDateInput: React.FC<IMaskedDateInputProps> = ({
    value,
    onChange,
    minDate,
    maxDate,
    excludeDate,
    invalid,
    disabled,
    readOnly,
    clearable = true,
    compactActionsWhenClear = false,
    placeholder,
    name,
    id,
    variant,
    className,
    leftSection,
    popoverProps,
    clearButtonProps,
    'data-testid': dataTestid,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    ref,
    calendarPosition = 'left',
    onBlur,
    onFocus,
    onKeyDown,
    w = '100%',
}) => {
    const editable = !readOnly && !disabled;
    const [opened, setOpened] = useState(false);
    const portalTarget = usePortalTarget();

    const inputRef = useRef<HTMLInputElement | null>(null);
    const fieldA11y = useFieldA11y({
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-describedby': ariaDescribedBy,
    });
    // Последнее значение, которое МЫ эмитнули наверх — чтобы отличить собственное
    // эхо (value пришёл обратно от родителя) от внешнего изменения и не затирать
    // текст в процессе ручного ввода.
    const lastEmittedRef = useRef<string | null | undefined>(undefined);
    // Актуальный value без stale-closure в нативных колбэках useMask.
    const valueRef = useRef<string | null>(value ?? null);
    valueRef.current = value ?? null;

    const inRange = (iso: string): boolean => {
        const minIso = minDate != null ? dateToIsoDate(minDate as unknown) : null;
        const maxIso = maxDate != null ? dateToIsoDate(maxDate as unknown) : null;
        if (minIso && iso < minIso) return false;
        if (maxIso && iso > maxIso) return false;
        return true;
    };

    const emit = (next: string | null) => {
        if (next === valueRef.current || next === lastEmittedRef.current) return;
        lastEmittedRef.current = next;
        onChange?.(next);
    };

    const mask = useSyncedMask({
        mask: DATE_MASK,
        invalid,
        // Коммит на каждом изменении с заполненной маской, НЕ через onComplete:
        // тот стреляет только на переходе «не заполнено → заполнено», а при
        // перепечатке цифр поверх уже заполненной даты маска остаётся
        // заполненной — ввод не коммитился и откатывался на blur.
        onChangeRaw: (raw, masked) => {
            // Полная очистка (Backspace до пустоты / clear) → null. Частичный ввод
            // не эмитим: коммит — только полная валидная дата, иначе blur-откат.
            if (raw === '') {
                emit(null);
                return;
            }
            if (raw.length < 8) return;
            const iso = parseRuDate(masked);
            if (!iso || !inRange(iso)) return;
            emit(iso);
        },
    });

    // Текущий <input> (с маской или только для чтения). Внешний ref получает его эффектом, а не в merged-ref:
    // новая функция ref у потребителя пере-прикрепила бы ref маски и сбросила каретку (см. useSyncedMask).
    const fieldRef = useRef<HTMLInputElement | null>(null);
    const mergedRef = useMergedRef(mask.ref, inputRef, fieldRef);
    useEffect(() => {
        if (!ref) return undefined;
        assignRef(ref, fieldRef.current);
        return () => {
            assignRef(ref, null);
        };
    }, [ref, editable]);

    // Записать ISO в текст поля мимо ввода: без события input и без onChange.
    const pushText = (iso: string | null) => {
        const el = inputRef.current;
        if (!el) return;
        const text = iso ? isoDateToRu(iso) : '';
        if (el.value !== text) mask.write(text);
    };

    // value → текст. Своё эхо пропускаем: текст уже введён пользователем. После readOnly/disabled поле с маской —
    // новый пустой <input>, текст в него пишем и без смены value.
    useEffect(() => {
        const v = value ?? null;
        const isOwnEcho = v === lastEmittedRef.current;
        lastEmittedRef.current = undefined;
        if (!isOwnEcho) pushText(v);
    }, [value, editable]);

    const handleDayChange = (next: string | null) => {
        emit(next ?? null);
        pushText(next ?? null);
        setOpened(false);
        inputRef.current?.focus();
    };

    const handleClear = () => {
        emit(null);
        pushText(null);
        setOpened(false);
    };

    const showClear = clearable && editable && !!(value ?? '');
    const calendarIcon = leftSection ?? <MdCalendarMonth size={16} aria-hidden />;
    const calendarRight = calendarPosition === 'right';
    const showRightCalendar = calendarRight && !(compactActionsWhenClear && showClear);

    const sharedInputProps = {
        // Алиасим классы под старый Mantine DateInput, чтобы внешние стили,
        // таргетящие .mantine-DateInput-{wrapper,input,section} (паритет границ/высоты,
        // скрытие иконки в виджете Календаря и т.п.), продолжали работать.
        classNames: {
            wrapper: 'mantine-DateInput-wrapper',
            input: 'mantine-DateInput-input',
            section: 'mantine-DateInput-section',
        },
        variant,
        name,
        id,
        placeholder: placeholder ?? 'Выберите дату',
        leftSection: calendarRight ? undefined : calendarIcon,
        'data-error': invalid || undefined,
        // aria-invalid ставит сам Mantine (withAria) по error и затирает переданный; вид ошибки — от data-error поля
        // (у обёртки Mantine data-error перекрасил бы иконку)
        error: invalid || undefined,
        withErrorStyles: false,
        'data-testid': dataTestid,
        'aria-label': ariaLabel,
        'aria-labelledby': fieldA11y['aria-labelledby'],
        w,
        className,
        autoComplete: 'off' as const,
    };

    if (!editable) {
        return (
            <MantineDescribedBy ids={fieldA11y['aria-describedby']}>
                <Input
                    {...sharedInputProps}
                    ref={fieldRef}
                    readOnly={readOnly}
                    disabled={disabled}
                    value={isoDateToRu(value)}
                    onChange={() => undefined}
                />
            </MantineDescribedBy>
        );
    }

    return (
        <Popover
            opened={opened}
            trapFocus={false}
            withRoles={false}
            position="bottom-start"
            shadow="md"
            zIndex={POPOVER_Z_INDEX}
            middlewares={{ flip: true, shift: true, inline: false }}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
            {...popoverProps}
        >
            <Popover.Target>
                <div style={{ width: '100%' }}>
                    <MantineDescribedBy ids={fieldA11y['aria-describedby']}>
                        <Input
                            {...sharedInputProps}
                            ref={mergedRef}
                            rightSection={
                                showClear || showRightCalendar ? (
                                    <span
                                        className="rf-masked-date-input__actions"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}
                                    >
                                        {showClear && (
                                            <CloseButton
                                                size="sm"
                                                variant="transparent"
                                                aria-label="Очистить"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={handleClear}
                                                {...clearButtonProps}
                                            />
                                        )}
                                        {showRightCalendar && (
                                            // клик по иконке = клик по полю: открыть календарь и дать фокус
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    cursor: 'pointer',
                                                }}
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => {
                                                    setOpened(true);
                                                    inputRef.current?.focus();
                                                }}
                                                aria-hidden
                                            >
                                                {calendarIcon}
                                            </span>
                                        )}
                                    </span>
                                ) : undefined
                            }
                            rightSectionPointerEvents={showClear || showRightCalendar ? 'all' : 'none'}
                            rightSectionWidth={showRightCalendar ? (showClear ? 52 : 30) : showClear ? 30 : undefined}
                            onFocus={(e) => {
                                setOpened(true);
                                onFocus?.(e);
                            }}
                            onClick={() => setOpened(true)}
                            onBlur={(e) => {
                                // Клики по календарю не блюрят (onMouseDown preventDefault на
                                // dropdown), поэтому blur = ушли в другое поле/наружу → закрываем.
                                // Заодно fixOnBlur: неполный/невалидный ввод откатываем к коммиту.
                                setOpened(false);
                                pushText(valueRef.current);
                                onBlur?.(e);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    setOpened(false);
                                    e.currentTarget.blur();
                                } else if (e.key === 'Escape') {
                                    // Первый Escape закрывает календарь, а не окно или панель вокруг поля (стек окон
                                    // пропускает погашенный Escape).
                                    if (opened) e.preventDefault();
                                    setOpened(false);
                                }
                                onKeyDown?.(e);
                            }}
                        />
                    </MantineDescribedBy>
                </div>
            </Popover.Target>
            <Popover.Dropdown onMouseDown={(e) => e.preventDefault()} data-dates-dropdown p="sm">
                <DatePicker
                    value={value ?? null}
                    defaultDate={value ?? undefined}
                    onChange={handleDayChange}
                    minDate={minDate}
                    maxDate={maxDate}
                    excludeDate={excludeDate}
                />
            </Popover.Dropdown>
        </Popover>
    );
};

export default MaskedDateInput;
